import { PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Group, Matrix4, Vector3, Vector3Like } from 'three'
import { HeightIndicator, HeightRef } from './HeightIndicator'
import { scaleModelVectorToUI, scaleUIVectorToModel, vectorModelToUI } from '~/components/DrawingCanvas/util/units'
import { useAppSelector } from '~/store/hooks'
import { Product } from '~/store/objects/types'
import { ArrowHandle, DimensionGuides } from './DimensionGuides'
import { MODEL_X_ROTATION_OFFSET } from '~/components/DrawingCanvas/Products/constants'
import { DragControls, DragControlsProps } from '../../util/DragControls'

type ProductDragControlsProps = {
  product: Product
  onDrag?: (modelPosition: Matrix4, isMultipleSelected?: boolean) => void
  /** Returns a boolean indicating whether or not the new position is valid */
  onUpdatePosition: (newPosition: Vector3Like) => boolean
  enabled?: boolean
}
export const ProductDragControls = ({
  product,
  onDrag,
  onUpdatePosition,
  enabled,
  children,
}: PropsWithChildren<ProductDragControlsProps>) => {
  const selectedObjects = useAppSelector(state => state.selectedObjects)
  const dragRef = useRef<Group>(null!)
  const [isDragging, setIsDragging] = useState(false)
  const arrowsRef = useRef<ArrowHandle>(null)
  const heightIndicatorRef = useRef<HeightRef>(null)

  const isSelected = Boolean(selectedObjects.find(({ id }) => id === product.id))
  const isOnlySelected = isSelected && selectedObjects.length === 1
  const { layerKey, isDirectionalOverhead, isForcedWallMount } = product
  const isVisible = useAppSelector(state => state.layers.layers[layerKey].visible)
  const isHeater = layerKey === 'PRODUCTS_HEATERS'
  const isOverhead = layerKey === 'PRODUCTS_OVERHEAD'
  const isShowHeightIndicator =
    Boolean(isHeater || isOverhead || isDirectionalOverhead || isForcedWallMount) && isVisible

  const origin = new Vector3()
  const { x, y, z } = product.position

  useEffect(() => {
    if (!isSelected) return
    const controller = new AbortController()
    const newPosition = new Vector3()
    window.addEventListener(
      'multidrag',
      ({ detail }) => {
        newPosition.setFromMatrixPosition(dragRef.current.matrix)
        newPosition.add(detail.delta)
        dragRef.current.matrixAutoUpdate = false
        dragRef.current.matrix.setPosition(newPosition)
        onDrag?.(dragRef.current.matrix, true)
      },
      { signal: controller.signal }
    )
    return () => controller.abort()
  }, [isSelected, onDrag])

  useLayoutEffect(() => {
    dragRef.current.matrix.setPosition(vectorModelToUI({ x, y, z }))
  }, [x, y, z])

  const handleDragStart = () => setIsDragging(true)

  const handleDrag: DragControlsProps['onDrag'] = (_, delta) => {
    origin.copy(product.position)
    scaleModelVectorToUI(origin)
    origin.add(delta)
    dragRef.current.matrixAutoUpdate = false
    dragRef.current.matrix.setPosition(origin)
    if (onDrag) {
      onDrag(dragRef.current.matrix)
      origin.setFromMatrixPosition(dragRef.current.matrix)
    }
    arrowsRef.current?.setOrigin(origin)
    heightIndicatorRef.current?.updateHeight(origin.z)
  }

  const handleDragEnd = () => {
    setIsDragging(false)
    const { x, y, z } = scaleUIVectorToModel(new Vector3().setFromMatrixPosition(dragRef.current.matrix))
    const isSuccessfulUpdate = onUpdatePosition({ x, y, z })
    if (!isSuccessfulUpdate) dragRef.current.matrix.setPosition(vectorModelToUI(product.position))
  }

  return (
    <DragControls
      dragDisabled={enabled !== undefined ? !enabled : !isOnlySelected}
      ref={dragRef}
      position={vectorModelToUI(product.position)}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
    >
      {isOnlySelected && (
        <DimensionGuides {...product} ref={arrowsRef} isDragging={isDragging} onUpdatePosition={onUpdatePosition} />
      )}
      {isShowHeightIndicator && <HeightIndicator height={product.position.z} ref={heightIndicatorRef} />}
      <group rotation-x={MODEL_X_ROTATION_OFFSET}>{children}</group>
    </DragControls>
  )
}
